import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ChevronRight, Search } from 'lucide-react';
import { fetchServices, searchServices } from '../../services/wpApi';
import { mapWordPressServicesToServices, Service } from '../../utils/serviceMapper.tsx';
import { getAllMainCategories, getSubCategoriesForMainCategory, ServiceCategory } from '../../utils/serviceCategories';

// Export Service interface for use in other components
export type { Service };

interface ServicesHomeProps {
  onServiceSelect?: (service: Service) => void;
  onConsultClick?: () => void;
  initialSearchQuery?: string;
}

export default function ServicesHome({
  onServiceSelect,
  onConsultClick,
  initialSearchQuery = ''
}: ServicesHomeProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]); // Store all services for category filtering
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const mainCategories = getAllMainCategories();

  // Load all services on mount
  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const wpServices = await fetchServices();
        // Ensure wpServices is an array
        const servicesArray = Array.isArray(wpServices) ? wpServices : [];
        const mappedServices = mapWordPressServicesToServices(servicesArray);
        setServices(mappedServices);
        setAllServices(mappedServices); // Store all services
         } catch (err: any) {
               console.error('Error loading services:', err);
               console.error('Error details:', {
                 message: err?.message,
                 stack: err?.stack
               });
               setError('Failed to load services. Please try again later.');
               // Fallback to empty array - components will handle gracefully
               setServices([]);
               setAllServices([]);
             } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []);

  // Set initial search query if provided
  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Search WordPress API when search query changes (with debounce)
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If search is empty, show all services
      setServices(allServices);
      return;
    }

    // Debounce search to avoid too many API calls
    const searchTimeout = setTimeout(async () => {
      try {
        setSearching(true);
        setError(null);
        const wpServices = await searchServices(searchQuery);
        const servicesArray = Array.isArray(wpServices) ? wpServices : [];
        const mappedServices = mapWordPressServicesToServices(servicesArray);
        setServices(mappedServices);
      } catch (err) {
        console.error('Error searching services:', err);
        // Fallback to local filtering if API search fails
        const filtered = allServices.filter(service => 
          service.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
          service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.nameAr.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setServices(filtered);
      } finally {
        setSearching(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, allServices]);

  // Filter services by main category and sub-category
  const filteredServices = services.filter(service => {
    // Filter by main category
    if (selectedMainCategory !== 'all' && service.mainCategoryId !== selectedMainCategory) {
      return false;
    }
    // Filter by sub-category
    if (selectedSubCategory !== 'all' && service.subCategoryId !== selectedSubCategory) {
      return false;
    }
    return true;
  });

  // Get sub-categories for selected main category
  const availableSubCategories = selectedMainCategory === 'all' 
    ? [] 
    : getSubCategoriesForMainCategory(selectedMainCategory);

  // Count services per category
  const getServiceCount = (categoryId: string, isSubCategory: boolean = false) => {
    if (categoryId === 'all') return services.length;
    if (isSubCategory) {
      return services.filter(s => s.subCategoryId === categoryId).length;
    }
    return services.filter(s => s.mainCategoryId === categoryId).length;
  };

  // Reset sub-category when main category changes
  useEffect(() => {
    if (selectedMainCategory === 'all') {
      setSelectedSubCategory('all');
    } else {
      // Keep sub-category if it's still valid, otherwise reset
      const subCats = getSubCategoriesForMainCategory(selectedMainCategory);
      if (!subCats.find(sc => sc.id === selectedSubCategory)) {
        setSelectedSubCategory('all');
      }
    }
  }, [selectedMainCategory]);
  return <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our Services
            </h1>
            <p className="text-xl text-white/90 mb-2">
              خدماتنا المتخصصة
            </p>
            <p className="text-lg text-white/80 mb-8">
              Comprehensive IT and cybersecurity solutions designed for the modern enterprise
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <input type="text" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-6 py-4 pl-14 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>
          </motion.div>
        </div>
        
        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48h1440V0c-240 48-480 48-720 24C480 0 240 0 0 24v24z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          {/* Main Category Filters */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">Main Categories:</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSelectedMainCategory('all');
                  setSelectedSubCategory('all');
                }}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedMainCategory === 'all'
                    ? 'bg-[#007A3D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Services ({getServiceCount('all')})
              </button>
              {mainCategories.map((category) => {
                const count = getServiceCount(category.id);
                if (count === 0) return null;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedMainCategory(category.id);
                      setSelectedSubCategory('all');
                    }}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      selectedMainCategory === category.id
                        ? 'bg-[#007A3D] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sub-Category Filters (only show if main category is selected) */}
          {selectedMainCategory !== 'all' && availableSubCategories.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Sub-Categories:</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedSubCategory('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedSubCategory === 'all'
                      ? 'bg-[#005B2A] text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All ({getServiceCount(selectedMainCategory)})
                </button>
                {availableSubCategories.map((subCategory) => {
                  const count = getServiceCount(subCategory.id, true);
                  if (count === 0) return null;
                  return (
                    <button
                      key={subCategory.id}
                      onClick={() => setSelectedSubCategory(subCategory.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedSubCategory === subCategory.id
                          ? 'bg-[#005B2A] text-white'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {subCategory.name} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {(loading || searching) && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007A3D]"></div>
              <p className="mt-4 text-gray-600">{searching ? 'Searching services...' : 'Loading services...'}</p>
            </div>
          )}

          {error && !loading && !searching && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <p className="text-gray-600 text-sm">Using fallback data or please check your WordPress API connection.</p>
            </div>
          )}

          {!loading && !searching && !error && filteredServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {searchQuery ? `No services found matching "${searchQuery}"` : 'No services found matching your search.'}
              </p>
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')} 
                  className="mt-4 text-[#007A3D] hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {!loading && filteredServices.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5
          }} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group overflow-hidden" onClick={() => onServiceSelect?.(service)}>
                {/* Color Bar */}
                <div className="h-2 w-full" style={{
              backgroundColor: service.color
            }} />
                
                <div className="p-6">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{
                backgroundColor: `${service.color}20`
              }}>
                    <div style={{
                  color: service.color
                }}>
                      {service.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {service.nameAr}
                  </p>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>

                  {/* Features Preview */}
                  <div className="space-y-2 mb-4">
                    {service.features.slice(0, 3).map((feature, i) => <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle size={16} style={{
                    color: service.color
                  }} />
                        <span>{feature}</span>
                      </div>)}
                  </div>

                  {/* CTA */}
                  <button className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors" style={{
                backgroundColor: service.color + '10',
                color: service.color
              }} onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = service.color;
                e.currentTarget.style.color = '#ffffff';
              }} onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = service.color + '10';
                e.currentTarget.style.color = service.color;
              }}>
                    Learn More
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.div>)}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-r from-[#007A3D] to-[#005B2A] rounded-3xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Need a Custom Solution?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Our team of experts can design a tailored IT strategy that fits your unique business needs
            </p>
            <button className="bg-[#D7182A] hover:bg-[#b51525] text-white px-8 py-4 rounded-full font-semibold transition-colors inline-flex items-center gap-2" onClick={onConsultClick}>
              Schedule Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>;
}